// /audit — paginated audit log viewer with filter bar (Wave 4)
// Spec: docs/plans/admin-tooling.md D-62

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button, Input, Field, ErrorChip, PageHeader } from '@awakenedlands/ui';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuditRow {
  id: number;
  staff_id: number;
  action: string;
  target_user_id: number | null;
  payload_json: string;
  ip: string;
  created_at: number;
  prev_hash: string;
  row_hash: string;
}

interface AuditResponse {
  rows: AuditRow[];
  next_cursor: string | null;
  chain_status: 'verified' | 'unverified';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildQueryString(
  staffId: string,
  targetUserId: string,
  action: string,
  cursor?: string,
): string {
  const params = new URLSearchParams();
  if (staffId.trim()) params.set('staff_id', staffId.trim());
  if (targetUserId.trim()) params.set('target_user_id', targetUserId.trim());
  if (action.trim()) params.set('action', action.trim());
  if (cursor) params.set('cursor', cursor);
  params.set('limit', '50');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Chain status banner (Step 4a) ─────────────────────────────────────────────

function ChainStatusBanner({
  status,
  rowCount,
}: {
  status: 'verified' | 'unverified';
  rowCount: number;
}) {
  if (status === 'verified') {
    return (
      <div
        role="status"
        className="flex items-center gap-4 rounded-sm border border-border border-l-2 border-l-[--color-status-ok] bg-panel p-4"
      >
        <ShieldCheck size={28} className="shrink-0 text-[--color-status-ok]" aria-hidden="true" />
        <div>
          <p className="font-semibold text-ink-high">Chain verified</p>
          <p className="text-sm text-ink-mid">
            All {rowCount} audit rows hash-link intact.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="flex items-center gap-4 rounded-sm border border-destructive border-l-2 border-l-destructive bg-destructive-soft/30 p-4"
    >
      <ShieldAlert size={28} className="shrink-0 text-destructive-strong" aria-hidden="true" />
      <div>
        <p className="font-semibold text-destructive-strong">
          Chain unverified
        </p>
        <p className="text-sm text-ink-mid">
          Hash chain integrity check FAILED — audit trail may be tampered. Escalate immediately.
        </p>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AuditPage() {
  // Filter form state (draft — applied only on "Filter" click)
  const [draftStaffId, setDraftStaffId] = useState('');
  const [draftTargetUserId, setDraftTargetUserId] = useState('');
  const [draftAction, setDraftAction] = useState('');

  // Applied filters (trigger query)
  const [appliedStaffId, setAppliedStaffId] = useState('');
  const [appliedTargetUserId, setAppliedTargetUserId] = useState('');
  const [appliedAction, setAppliedAction] = useState('');

  // Pagination / accumulated rows
  const [cursor, setCursor] = useState<string | null>(null);
  const [accRows, setAccRows] = useState<AuditRow[]>([]);
  const [chainStatus, setChainStatus] = useState<'verified' | 'unverified' | null>(null);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);

  // Initial query (driven by applied filters; cursor excluded so it re-runs on filter change)
  const { data, isLoading, isError } = useQuery<AuditResponse>({
    queryKey: ['audit', appliedStaffId, appliedTargetUserId, appliedAction],
    queryFn: async () => {
      const qs = buildQueryString(appliedStaffId, appliedTargetUserId, appliedAction);
      const res = await apiFetch<AuditResponse>(`/admin/audit${qs}`);
      if (!res.ok) throw new Error('Failed to load audit log.');
      // Reset accumulated state whenever a fresh query runs
      setAccRows(res.data.rows);
      setCursor(res.data.next_cursor);
      setChainStatus(res.data.chain_status);
      return res.data;
    },
  });

  function handleFilter() {
    setAppliedStaffId(draftStaffId);
    setAppliedTargetUserId(draftTargetUserId);
    setAppliedAction(draftAction);
  }

  async function handleLoadMore() {
    if (!cursor) return;
    setLoadMoreBusy(true);
    const qs = buildQueryString(appliedStaffId, appliedTargetUserId, appliedAction, cursor);
    const res = await apiFetch<AuditResponse>(`/admin/audit${qs}`);
    setLoadMoreBusy(false);
    if (!res.ok) return;
    setAccRows((prev) => [...prev, ...res.data.rows]);
    setCursor(res.data.next_cursor);
    setChainStatus(res.data.chain_status);
  }

  const displayedRows: AuditRow[] = accRows.length > 0 ? accRows : (data?.rows ?? []);
  const effectiveChainStatus = chainStatus ?? data?.chain_status;
  const hasRows = displayedRows.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description="Tamper-evident log of all staff actions."
      />

      {/* Chain status banner (Step 4a) — shown above filter bar whenever status known */}
      {effectiveChainStatus && (
        <ChainStatusBanner status={effectiveChainStatus} rowCount={displayedRows.length} />
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 items-end rounded-sm border border-border bg-muted p-4">
        <Field label="Staff ID">
          <Input
            id="audit-staff-id"
            type="number"
            placeholder="e.g. 1"
            value={draftStaffId}
            onChange={(e) => setDraftStaffId(e.target.value)}
            className="w-28"
          />
        </Field>
        <Field label="Target User ID">
          <Input
            id="audit-target-user-id"
            type="number"
            placeholder="e.g. 42"
            value={draftTargetUserId}
            onChange={(e) => setDraftTargetUserId(e.target.value)}
            className="w-28"
          />
        </Field>
        <Field label="Action">
          <Input
            id="audit-action"
            type="text"
            placeholder="e.g. account-lock"
            value={draftAction}
            onChange={(e) => setDraftAction(e.target.value)}
            className="w-44"
          />
        </Field>
        <Button type="button" onClick={handleFilter} disabled={isLoading}>
          Filter
        </Button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip>Failed to load audit log.</ErrorChip>
      )}

      {/* Empty state */}
      {!isLoading && !isError && !hasRows && (
        <p className="text-sm text-ink-low">No audit rows found.</p>
      )}

      {/* Table */}
      {!isLoading && !isError && hasRows && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Date</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Staff ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Action</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Target User</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {displayedRows.map((row) => (
                  <tr key={row.id} className="hover:bg-panel-elev" data-testid={`audit-row-${row.id}`}>
                    <td className="px-3 py-2 text-ink-low font-mono text-xs tabular-nums">{row.id}</td>
                    <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                      {new Date(row.created_at * 1000).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{row.staff_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.action}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.target_user_id !== null ? row.target_user_id : '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-low">{row.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden">
            {displayedRows.map((row) => (
              <div
                key={row.id}
                className="rounded-sm border border-border bg-panel p-3"
                data-testid={`audit-card-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className="font-mono text-xs font-medium text-ink-high">{row.action}</span>
                  <span className="font-mono text-xs text-ink-low tabular-nums">#{row.id}</span>
                </div>
                <div className="space-y-0.5 text-xs text-ink-low font-mono">
                  <div>
                    <span className="text-ink-mid">Staff </span>
                    <span className="tabular-nums">{row.staff_id}</span>
                    {row.target_user_id !== null && (
                      <>
                        <span className="text-ink-mid"> · Target </span>
                        <span className="tabular-nums">{row.target_user_id}</span>
                      </>
                    )}
                  </div>
                  <div className="tabular-nums">{new Date(row.created_at * 1000).toLocaleString()}</div>
                  <div>{row.ip}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Load more */}
          {cursor && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { void handleLoadMore(); }}
                disabled={loadMoreBusy}
              >
                {loadMoreBusy ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-low border-t-primary" />
                    Loading…
                  </span>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
